import { useEffect, useState } from 'react';

/**
 * Element id on AppShell's `.contentArea` — the wrapper around the scrollable
 * content pane, sitting beside the sidebar. It is the containing block for
 * page-level overlays (see AppShell.module.scss).
 */
export const SHELL_OVERLAY_TARGET_ID = 'appShellContentArea';

/**
 * Toggled on `<html>` by AppShell whenever the chat panel is reserving width
 * on the right. Overlays read the resulting `--app-chat-reserved` custom
 * property; see AppShell.module.scss for why it can't just be inherited.
 */
export const CHAT_RESERVED_CLASS = 'fs-chat-reserved';

/** App chrome that stays put around a content-area overlay. */
export const SHELL_HEADER_ID = 'appShellHeader';
export const SHELL_SIDEBAR_ID = 'appShellSidebar';

/**
 * Marks a control inside the chrome whose effect is visible *without* closing a
 * content-area overlay — the AI Agent toggle (the panel sits beside the
 * overlay), the sidebar collapse chevron (the rail is beside it), the prompt bar
 * in AI mode (the answer lands in the panel).
 *
 * Everything else in the header or sidebar acts on what the overlay is
 * covering, or opens a menu that would paint underneath it, so an overlay
 * dismisses itself when those are used. Opting out is per-control and explicit
 * so a new chrome control gets the safe behaviour by default.
 */
export const KEEP_OVERLAY_ATTR = 'data-keep-asset-overlay';

/**
 * The node a full-area overlay should portal into, or `null` outside the app
 * shell (login, share pages, Storybook), where the caller should fall back to
 * rendering in place / to `<body>`.
 *
 * Why a portal at all: an overlay has to escape the scrollable `<main>`, since
 * an `absolute; inset:0` child of a scroll container is positioned against the
 * scrolled padding box and drifts up as the user scrolls. Portaling into
 * `.contentArea`, which never scrolls, pins it to the content pane exactly.
 *
 * Resolved in an effect rather than at module scope: on the very first render
 * of an overlay mounted alongside AppShell the target may not be in the DOM
 * yet, and `useState(() => getElementById(...))` would cache that `null`
 * forever. One extra render, no stale target.
 */
export function useShellOverlayTarget(): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTarget(document.getElementById(SHELL_OVERLAY_TARGET_ID));
  }, []);

  return target;
}

export default useShellOverlayTarget;
